import { TbAlertCircle, TbDownload } from "solid-icons/tb";
import { createSignal, Match, Switch } from "solid-js";
import { type MediaStorageLocation, useChatRuntime } from "../runtime/ChatRuntime";
import { ProcessingSpinner } from "../ui";

interface MediaDownloadButtonProps {
  location: MediaStorageLocation;
  /** Default filename offered in the save dialog (host may let the user edit it). */
  filename: string;
}

/**
 * Icon-only download control for inline generated media (image/chart), matching
 * the web app's artifact-row button. Renders only when the host exposes the
 * `downloadMedia` action, and routes bytes through that seam so hosts whose
 * webview can't fetch (chief) still download.
 */
export function MediaDownloadButton(props: MediaDownloadButtonProps) {
  const rt = useChatRuntime();
  const [downloading, setDownloading] = createSignal(false);
  const [errored, setErrored] = createSignal(false);

  const handleDownload = async (e: MouseEvent) => {
    e.stopPropagation();
    const download = rt.actions.downloadMedia;
    if (!download || downloading()) return;
    rt.actions.trackFeature?.({ feature: "chat.media_download", via: "button" });
    setErrored(false);
    setDownloading(true);
    try {
      await download({ location: props.location, filename: props.filename });
    } catch {
      setErrored(true);
    } finally {
      setDownloading(false);
    }
  };

  const label = () => (errored() ? rt.t("common.failed") : rt.t("tooltip_download"));

  return (
    <button
      type="button"
      onClick={(e) => void handleDownload(e)}
      disabled={downloading()}
      aria-label={label()}
      title={label()}
      class="group/btn flex shrink-0 cursor-pointer items-center justify-center rounded-lg border border-muted-border bg-tile p-1.5 shadow-md transition-all hover:bg-tile-hover disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Switch
        fallback={
          <TbDownload
            size={16}
            class="stroke-2 transition-transform group-hover/btn:scale-hover group-active/btn:scale-active"
          />
        }
      >
        <Match when={downloading()}>
          <ProcessingSpinner class="size-3.5" />
        </Match>
        <Match when={errored()}>
          <TbAlertCircle class="size-4 text-red-500 dark:text-red-400" />
        </Match>
      </Switch>
    </button>
  );
}
